import { BuildTypeCode } from '../../projects/project';
import {
  DataForQuoteOrDesignForm,
  HeatPumpUnitFormModel,
  MicrogenerationCertificationSchemeLocationFormModel,
} from '../quote-or-design-form/GetDataForQuoteOrDesignForm';
import { percentageCoverageOfHeatPumpUnits } from '../quote-or-design-form/heat-pump/heatPumpCalculations';
import { HeatPumpFormModel } from '../quote-or-design-form/heat-pump/heatPumpFormModel';
import {
  GroundCollectorTypeCode,
  HeatPumpTypeCode,
  SwimmingPoolTypeCode,
} from '../quote-or-design-form/quoteOrDesignFormData';

export type HeatPumpResponse = {
  heatPumpComponentId: number;
  heatPumpTypeCode: HeatPumpTypeCode;
  groundCollectorTypeCode: GroundCollectorTypeCode | null;
  groundCollectorLengthInMetres: number | null;
  closestMicrogenerationCertificationSchemeLocationName: string | null;
  isCoastalLocation: boolean;
  shouldAddUserHeatEmitter: boolean;
  hasPrivateWaterSupply: boolean;
  areaInMetresSquared: number | null;
  heatLossRateInWattsPerSquareMetre: number | null;
  buildingHeatLoadInKilowatts: number | null;
  swimmingPoolTypeCode: SwimmingPoolTypeCode;
  swimmingPoolEnergyConsumptionInKilowattHours: number | null;
  swimmingPoolPowerInKilowatts: number | null;
  heatPumpUnitPartIds: Array<number>;
  dockingCode: string | null;
  supportOptionPartId: number;
};

export const mapHeatPumpResponseToFormModel = (
  heatPumpResponse: HeatPumpResponse,
  parts: DataForQuoteOrDesignForm,
  projectBuildType: BuildTypeCode,
): HeatPumpFormModel => {
  const heatPumpUnitParts = heatPumpResponse.heatPumpUnitPartIds.map(partId =>
    getHeatPumpUnitPartOrNull(partId, parts),
  );
  const closestMicrogenerationCertificationSchemeLocation = getClosestMicrogenerationCertificationSchemeLocationOrNull(
    heatPumpResponse,
    parts,
  );

  return {
    isExpanded: true,
    heatPumpTypeCode: heatPumpResponse.heatPumpTypeCode,
    groundCollectorTypeCode: heatPumpResponse.groundCollectorTypeCode || 'TwentyFiveMillimetres',
    groundCollectorLengthInMetres: heatPumpResponse.groundCollectorLengthInMetres?.toString() ?? '',
    closestMicrogenerationCertificationSchemeLocation,
    isCoastalLocation: heatPumpResponse.isCoastalLocation,
    shouldAddUserHeatEmitter: heatPumpResponse.shouldAddUserHeatEmitter,
    hasPrivateWaterSupply: heatPumpResponse.hasPrivateWaterSupply,
    areaInMetresSquared: heatPumpResponse.areaInMetresSquared?.toString() ?? '',
    heatLossRateInWattsPerSquareMetre: heatPumpResponse.heatLossRateInWattsPerSquareMetre?.toString() ?? '',
    buildingHeatLoadInKilowatts: heatPumpResponse.buildingHeatLoadInKilowatts?.toString() ?? '',
    swimmingPoolTypeCode: heatPumpResponse.swimmingPoolTypeCode,
    swimmingPoolEnergyConsumptionInKilowattHours:
      heatPumpResponse.swimmingPoolEnergyConsumptionInKilowattHours?.toString() ?? '',
    swimmingPoolPowerInKilowatts: heatPumpResponse.swimmingPoolPowerInKilowatts?.toString() ?? '',
    heatPumpUnitParts,
    percentageCoverage: percentageCoverageOfHeatPumpUnits(
      heatPumpResponse.buildingHeatLoadInKilowatts,
      heatPumpUnitParts,
      closestMicrogenerationCertificationSchemeLocation,
      projectBuildType,
    ),
    dockingCode: heatPumpResponse.dockingCode,
    requiredHeatPumpUnitCount: getRequiredHeatPumpUnitCount(heatPumpResponse.dockingCode, parts),
    supportOptionPartId: heatPumpResponse.supportOptionPartId,
  };
};

export const getRequiredHeatPumpUnitCount = (
  dockingCode: string | null,
  parts: DataForQuoteOrDesignForm,
): number | null => (dockingCode == null ? null : parts.requiredHeatPumpUnitCountByDockingCode[dockingCode] ?? null);

const getClosestMicrogenerationCertificationSchemeLocationOrNull = (
  heatPumpResponse: HeatPumpResponse,
  parts: DataForQuoteOrDesignForm,
): MicrogenerationCertificationSchemeLocationFormModel | null => {
  const location = parts.microgenerationCertificationSchemeLocationOptions.find(
    option => option.text === heatPumpResponse.closestMicrogenerationCertificationSchemeLocationName,
  );
  return location == null ? null : location.value;
};

const getHeatPumpUnitPartOrNull = (
  heatPumpUnitPartId: number,
  parts: DataForQuoteOrDesignForm,
): HeatPumpUnitFormModel | null => {
  const matchingOption = parts.heatPumpUnitOptions.find(
    option => option.value && option.value.partId === heatPumpUnitPartId,
  );

  return matchingOption == null ? null : matchingOption.value;
};
